import { useCallback, useMemo } from 'react';
import {
  BarChart2,
  Handshake,
  Home,
  LayersIcon,
  MailIcon,
  Settings,
} from 'lucide-react';
import type { DashboardConfig } from '@xanectech/components/layout';
import { createLogger } from '@xanectech/lib';
const logger = createLogger('Company - useCompanyDashboardConfig');
import { useAuth } from '@xanectech/auth';
import { useUserProfile } from '@xanectech/auth';
import { useNavigate } from 'react-router-dom';
import {
  useCompany,
  useUnreadMessageCount,
  useMessages,
} from '@xanectech/data';

type CompanyDashboardConfigStatus =
  | { status: 'loading' }
  | { status: 'authenticated'; config: DashboardConfig }
  | { status: 'unauthenticated' }
  | {
      status: 'access-denied';
      currentRole?: string | null;
      reason: string;
    };

export function useCompanyDashboardConfig(): CompanyDashboardConfigStatus {
  const { signOut, user: authUser } = useAuth();
  const { profile, isLoading } = useUserProfile();
  const navigate = useNavigate();

  // Fetch unread message count
  const { data: unreadCount = 0 } = useUnreadMessageCount(
    profile?.userId,
    profile?.companyId ?? undefined,
  );

  const { data: company, isLoading: isLoadingCompany } = useCompany(
    profile?.companyId ?? undefined,
  );

  // Fetch recent messages for notifications
  const { data: messages = [] } = useMessages({
    userId: profile?.userId ?? '',
    companyId: profile?.companyId ?? undefined,
    userRole: profile?.primaryRole ?? '',
    unreadOnly: false,
  });

  // Convert messages to notifications
  const notifications = useMemo(() => {
    return messages.slice(0, 5).map((message) => ({
      id: message.id,
      title: String(message.title),
      description: String(message.content).substring(0, 100),
      timestamp: new Date(String(message.created_at)).toLocaleString(),
      read: message.read,
    }));
  }, [messages]);

  const handleLogout = useCallback(async () => {
    await signOut();
    logger.info('User logged out', { context: 'company', action: 'logout' });
    void navigate('/logout');
  }, [navigate, signOut]);
  // Profile is now auto-fetched by useUserProfile when user is authenticated

  const companyRole = useMemo(
    () =>
      profile?.companyId &&
      profile.isActive &&
      (profile.isCompanyAdmin ||
        profile.isDataAdministrator ||
        profile.isViewer)
        ? profile.primaryRole
        : null,
    [profile],
  );

  const companyDisplayName = useMemo(() => {
    const rawName = (company?.name ?? profile?.companyName ?? '').trim();
    return rawName.length > 0 ? rawName.split(/\s+/)[0] : '';
  }, [company?.name, profile?.companyName]);

  const companyLogoUrl = useMemo(() => {
    const logo = (company?.logo_url ?? profile?.companyLogoUrl ?? '').trim();
    return logo.length > 0 ? logo : null;
  }, [company?.logo_url, profile?.companyLogoUrl]);

  const config = useMemo<DashboardConfig | null>(() => {
    if (!profile || !companyRole || !companyDisplayName || !companyLogoUrl) {
      return null;
    }

    return {
      branding: {
        name: companyDisplayName,
        logo: companyLogoUrl,
        bgColor: 'bg-primary-gradient',
        textColor: 'text-primary',
        logoCollapsed: companyLogoUrl,
      },
      basePath: '/',
      singleExpanded: true,
      navigation: [
        {
          label: 'Main',
          collapsable: false,
          items: [
            { title: 'Dashboard', href: '/', icon: Home },
            {
              title: 'Messages',
              href: '/messages',
              icon: MailIcon,
              badge: unreadCount > 0 ? unreadCount : undefined,
              showBreadcrumbs: false,
            },
            {
              title: 'Analytics',
              href: '/analytics',
              icon: BarChart2,
              showBreadcrumbs: false,
            },
            {
              title: 'Settings',
              href: '/settings',
              icon: Settings,
              showBreadcrumbs: false,
            },
          ],
        },
        {
          label: 'Management',
          collapsable: true,
          items: [
            {
              title: 'Divisions',
              href: '/divisions',
              icon: Home,
              showBreadcrumbs: false,
            },
            {
              title: 'Users',
              href: '/users',
              icon: Home,
              showBreadcrumbs: false,
            },
          ],
        },
        {
          label: 'Data Contracts',
          collapsable: true,
          items: [
            {
              title: 'Contracts',
              href: '/data-contracts',
              icon: Handshake,
            },
            {
              title: 'Data Categories',
              href: '/data-categories',
              icon: LayersIcon,
            },
          ],
        },
      ],
      notifications,
      onLogout: handleLogout,
      user: {
        name: `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim(),
        email: profile.email ?? '',
        initials:
          profile.firstName && profile.lastName
            ? `${profile.firstName.charAt(0)}${profile.lastName
                .charAt(0)
                .toUpperCase()}`
            : '',
      },
    };
  }, [
    companyDisplayName,
    companyLogoUrl,
    companyRole,
    handleLogout,
    notifications,
    profile,
    unreadCount,
  ]);

  // If no authenticated user, return unauthenticated
  if (!authUser) {
    return { status: 'unauthenticated' };
  }

  // If authenticated user but profile is still loading, return loading
  if (isLoading) {
    return { status: 'loading' };
  }

  if (profile?.companyId && isLoadingCompany) {
    return { status: 'loading' };
  }

  // If authenticated user but no profile loaded yet, return loading
  if (!profile) {
    return { status: 'loading' };
  }

  logger.info('User company roles', {
    userId: profile.userId,
    primaryRole: companyRole,
  });

  if (!config) {
    if (companyRole && (!companyDisplayName || !companyLogoUrl)) {
      return { status: 'loading' };
    }

    logger.warn('User does not have valid company role', {
      currentRole: profile.primaryRole,
      userId: profile.userId,
    });
    return {
      status: 'access-denied',
      currentRole: profile.primaryRole,
      reason: profile.isActive
        ? 'missing_company_portal_role'
        : 'inactive_user',
    };
  }

  return {
    status: 'authenticated',
    config,
  };
}
